import { useEffect, useState } from 'react';
import { PageBody, PageHeader, ScopeBar } from '../components/AppShell';
import {
  Button,
  Card,
  CardHeader,
  ErrorState,
  Icon,
  LevelBadge,
  Loading,
} from '../components/ui';
import { ApiError, api } from '../lib/api';
import { useApi } from '../lib/useApi';
import { scopeLabel } from '../lib/format';
import type {
  NodePrivileges,
  Privilege,
  PrivilegeScope,
  TreeNode,
} from '../lib/types';

/** The matrix, in the order and grouping the design lays out. */
const SECTIONS: Array<{ heading: string; rows: Array<[Privilege, string]> }> = [
  {
    heading: 'Applications',
    rows: [
      ['application.write', 'Create and edit an application'],
      ['application.submit', 'Submit to underwriting'],
      ['application.decide', 'Approve or decline'],
      ['application.board', 'Board into MX Connect'],
      ['application.price_below_floor', 'Price below the inherited floor'],
      ['application.see_decline_reason', 'See decline reasons'],
    ],
  },
  {
    heading: 'Ordering',
    rows: [
      ['equipment.order', 'Order equipment'],
      ['equipment.approve_over_limit', 'Approve a purchase over $1,500'],
      ['supplies.order', 'Order paper and supplies'],
    ],
  },
  {
    heading: 'Money',
    rows: [
      ['residual.view', 'View residual reports'],
      ['residual.set_splits', 'Set splits for children'],
      ['residual.export', 'Export residual data'],
    ],
  },
  {
    heading: 'Administration',
    rows: [
      ['org.manage_users', 'Administer people'],
      ['org.invite', 'Invite people'],
      ['org.move_node', 'Move a node in the hierarchy'],
      ['audit.view', 'View the audit log'],
    ],
  },
];

/** The standard role matrix, mirrored from the backend for display. */
const STANDARD: Record<string, Partial<Record<Privilege, PrivilegeScope>>> = {
  'L1 Admin': Object.fromEntries(
    SECTIONS.flatMap((s) => s.rows.map(([p]) => [p, 'all' as PrivilegeScope])),
  ),
  'L1 Underwriter': {
    'application.decide': 'all',
    'application.board': 'all',
    'application.see_decline_reason': 'all',
    'audit.view': 'all',
  },
  'L2 Office': {
    'application.write': 'down',
    'application.submit': 'down',
    'application.board': 'down',
    'application.price_below_floor': 'down',
    'application.see_decline_reason': 'down',
    'equipment.order': 'down',
    'equipment.approve_over_limit': 'down',
    'supplies.order': 'down',
    'residual.view': 'down',
    'residual.set_splits': 'down',
    'residual.export': 'down',
    'org.invite': 'down',
    'org.manage_users': 'down',
    'org.move_node': 'down',
    'audit.view': 'down',
  },
  'L3 Manager': {
    'application.write': 'down',
    'application.submit': 'down',
    'application.price_below_floor': 'ask',
    'application.see_decline_reason': 'down',
    'equipment.order': 'down',
    'equipment.approve_over_limit': 'ask',
    'supplies.order': 'down',
    'residual.view': 'down',
    'residual.set_splits': 'down',
    'residual.export': 'ask',
    'org.invite': 'down',
    'org.manage_users': 'down',
    'audit.view': 'own',
  },
  'L4 Agent': {
    'application.write': 'own',
    'application.submit': 'own',
    'equipment.order': 'own',
    'supplies.order': 'own',
    'residual.view': 'own',
  },
  'Merchant support': { 'supplies.order': 'all' },
};

const ROLE_COLUMNS = Object.keys(STANDARD);

export function Permissions() {
  const tree = useApi<TreeNode>('/org/tree');

  // Default the override panel to the first child node — a node cannot adjust
  // its own privileges, only those of a node beneath it.
  const [nodeId, setNodeId] = useState<string | null>(null);
  useEffect(() => {
    if (!nodeId && tree.data?.children[0]) setNodeId(tree.data.children[0].id);
  }, [tree.data, nodeId]);

  const privileges = useApi<NodePrivileges>(
    nodeId ? `/org/nodes/${nodeId}/privileges` : null,
  );

  const [pending, setPending] = useState<Partial<Record<Privilege, PrivilegeScope>>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(
    null,
  );

  const selectedNode = tree.data?.children.find((c) => c.id === nodeId);

  async function saveOverride() {
    if (!nodeId || Object.keys(pending).length === 0) return;
    setSaving(true);
    setMessage(null);
    try {
      await api.put(`/org/nodes/${nodeId}/privileges`, { privileges: pending });
      setPending({});
      privileges.reload();
      setMessage({ tone: 'ok', text: 'Override saved. It takes effect at next login.' });
    } catch (err) {
      setMessage({
        tone: 'danger',
        text: err instanceof ApiError ? err.message : 'That override was rejected.',
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <PageHeader
        breadcrumb={['Administration', 'Roles & access']}
        title="Roles & privileges"
        actions={<Button>New custom role</Button>}
      />

      <ScopeBar>
        A node can grant only what it holds. Narrowing a parent narrows every child at
        the next login.
      </ScopeBar>

      <PageBody>
        <div className="split-2">
          <Card>
            <CardHeader
              title="Standard roles"
              action={
                <div className="legend">
                  {(['all', 'down', 'own', 'ask'] as PrivilegeScope[]).map((s) => (
                    <span className="legend__item" key={s}>
                      <span className={`scopechip scopechip--${s}`}>
                        {scopeLabel(s).short}
                      </span>
                      {scopeLabel(s).title.toLowerCase()}
                    </span>
                  ))}
                </div>
              }
            />

            <div className="table-wrap">
              <table className="table matrix">
                <thead>
                  <tr>
                    <th>Privilege</th>
                    {ROLE_COLUMNS.map((r) => (
                      <th key={r} style={{ textAlign: 'center' }}>
                        {r.split(' ').map((part, i) => (
                          <div key={i}>{part}</div>
                        ))}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {SECTIONS.map((section) => (
                    <>
                      <tr className="matrix__section" key={section.heading}>
                        <td colSpan={ROLE_COLUMNS.length + 1}>{section.heading}</td>
                      </tr>
                      {section.rows.map(([privilege, label]) => (
                        <tr key={privilege}>
                          <td>{label}</td>
                          {ROLE_COLUMNS.map((role) => {
                            const scope = STANDARD[role][privilege] ?? 'none';
                            const meta = scopeLabel(scope);
                            return (
                              <td key={role}>
                                <span
                                  className={`scopechip scopechip--${scope}`}
                                  title={meta.title}
                                >
                                  {meta.short}
                                </span>
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          <div className="stack">
            <Card>
              <CardHeader title="Three rules govern everything" />
              <ol className="steplist">
                <li>
                  <strong>Scope follows the tree.</strong> A record belongs to the node
                  that created it; every ancestor sees it, no sibling does.
                </li>
                <li>
                  <strong>You cannot grant what you lack.</strong> A parent’s privileges
                  are the ceiling for every child.
                </li>
                <li>
                  <strong>Revocation cascades.</strong> Narrow a parent and every
                  descendant narrows at next login.
                </li>
              </ol>
            </Card>

            <Card>
              <CardHeader
                title="Node override"
                action={
                  selectedNode ? <LevelBadge level={selectedNode.level} /> : undefined
                }
              />

              {tree.loading && <Loading />}
              {tree.data && tree.data.children.length === 0 && (
                <p className="card__meta">
                  You have no child nodes to adjust. A node cannot change its own
                  privileges.
                </p>
              )}

              {tree.data && tree.data.children.length > 0 && (
                <>
                  <div className="field" style={{ marginBottom: 14 }}>
                    <label className="field__label">Node</label>
                    <select
                      className="select"
                      value={nodeId ?? ''}
                      onChange={(e) => {
                        setNodeId(e.target.value);
                        setPending({});
                        setMessage(null);
                      }}
                    >
                      {tree.data.children.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name} ({c.level})
                        </option>
                      ))}
                    </select>
                  </div>

                  <p className="card__meta" style={{ marginBottom: 14 }}>
                    Start from the standard role and tighten it for this node only.
                    Anything left inherited tracks the parent automatically.
                  </p>

                  {privileges.loading && <Loading />}
                  {privileges.error && <ErrorState error={privileges.error} />}

                  {privileges.data && (
                    <div className="stack" style={{ gap: 10 }}>
                      {SECTIONS.flatMap((s) => s.rows).map(([privilege, label]) => {
                        const ceiling = privileges.data!.ceiling[privilege] ?? 'none';
                        const current =
                          pending[privilege] ??
                          privileges.data!.effective[privilege] ??
                          'none';
                        const isOverridden =
                          privileges.data!.override[privilege] !== undefined;
                        const locked = ceiling === 'none';

                        return (
                          <label
                            className="checkrow"
                            key={privilege}
                            style={{ opacity: locked ? 0.55 : 1 }}
                          >
                            <input
                              type="checkbox"
                              disabled={locked}
                              checked={current !== 'none'}
                              onChange={(e) =>
                                setPending((p) => ({
                                  ...p,
                                  // Restoring a privilege can only go as far as
                                  // the parent's own ceiling.
                                  [privilege]: e.target.checked ? ceiling : 'none',
                                }))
                              }
                            />
                            <span style={{ flexGrow: 1, minWidth: 0 }}>
                              <span style={{ display: 'block', color: 'var(--text)' }}>
                                {label}
                              </span>
                              <span className="check__detail">
                                {locked
                                  ? 'Not held by the parent — cannot be granted'
                                  : isOverridden
                                    ? 'Overridden on this node'
                                    : `Inherited · parent holds “${scopeLabel(ceiling).short}”`}
                              </span>
                            </span>
                            <span
                              className={`scopechip scopechip--${locked ? 'none' : isOverridden ? 'ask' : 'down'}`}
                            >
                              {locked ? 'Locked' : isOverridden ? 'Override' : 'Inherited'}
                            </span>
                          </label>
                        );
                      })}

                      {message && (
                        <div className={`notice notice--${message.tone}`} role="status">
                          {message.tone === 'ok'
                            ? Icon.check({ size: 15 })
                            : Icon.alert({ size: 15 })}
                          <span>{message.text}</span>
                        </div>
                      )}

                      <div className="row">
                        <Button
                          variant="primary"
                          disabled={saving || Object.keys(pending).length === 0}
                          onClick={() => void saveOverride()}
                        >
                          {saving ? 'Saving…' : 'Save override'}
                        </Button>
                        <Button
                          disabled={Object.keys(pending).length === 0}
                          onClick={() => {
                            setPending({});
                            setMessage(null);
                          }}
                        >
                          Reset
                        </Button>
                      </div>

                      <p className="login__footnote">
                        Every change here is written to the audit log with your name, and
                        takes effect for the affected people at their next request.
                      </p>
                    </div>
                  )}
                </>
              )}
            </Card>
          </div>
        </div>
      </PageBody>
    </>
  );
}
